import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { StageBadge } from '@/components/shared/stage-badge'
import { CopyCheck, RefreshCw, GitMerge, Undo2, X } from 'lucide-react'
import dayjs from 'dayjs'
import { toast } from 'sonner'
import { PageHeader } from '@/components/shared/page-header'
import { CustomersTabs } from './customers-tabs'
import { Loading, EmptyState, ErrorState } from '@/components/shared/feedback'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Badge } from '@/components/ui/badge'
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar'
import { initials } from '@/lib/utils'
import { apiError } from '@/lib/api-client'
import {
  stageBadgeVariant,
  stageLabel,
  useDismissDuplicateGroup,
  useDuplicateGroups,
  useMergeDuplicateGroup,
  useMergeHistory,
  useUndoMerge,
  type MergeLogItem,
  useScanDuplicates,
  type DuplicateContact,
  type DuplicateGroup,
} from '@/hooks/use-contacts'

export function DuplicateContactsPage() {
  const { t } = useTranslation('custList')
  const { data, isLoading, isError } = useDuplicateGroups()
  const scan = useScanDuplicates()

  const onScan = () => {
    scan.mutate(undefined, {
      onSuccess: (r) =>
        toast.success(t('duplicates.scanned', { scanned: r.scanned, newGroups: r.newGroups })),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <div className="space-y-6">
      <CustomersTabs />
      <PageHeader
        title={t('duplicates.title')}
        description={t('duplicates.description')}
        actions={
          <Button onClick={onScan} disabled={scan.isPending}>
            <RefreshCw className={`h-4 w-4 ${scan.isPending ? 'animate-spin' : ''}`} />
            {scan.isPending ? t('duplicates.scanning') : t('duplicates.scan')}
          </Button>
        }
      />

      {isLoading ? (
        <Loading label={t('duplicates.loading')} />
      ) : isError ? (
        <ErrorState />
      ) : !data?.groups.length ? (
        <EmptyState
          icon={CopyCheck}
          title={t('duplicates.emptyTitle')}
          description={t('duplicates.emptyDescription')}
        />
      ) : (
        <div className="space-y-4">
          {data.groups.map((g) => (
            <DuplicateGroupCard key={g.id} group={g} />
          ))}
        </div>
      )}

      <MergeHistory />
    </div>
  )
}

function MergeHistory() {
  const { t } = useTranslation('custList')
  const { data } = useMergeHistory()
  const undo = useUndoMerge()
  const merges = data?.merges ?? []
  if (merges.length === 0) return null

  const onUndo = (m: MergeLogItem) => {
    if (!confirm(t('duplicates.history.confirmUndo', { name: m.primaryName }))) return
    undo.mutate(m.id, {
      onSuccess: (r) => toast.success(t('duplicates.history.restored', { count: r.restored })),
      onError: (e) => toast.error(apiError(e)),
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">{t('duplicates.history.title')}</CardTitle>
      </CardHeader>
      <CardContent className="space-y-2">
        {merges.map((m) => (
          <div key={m.id} className="flex items-center gap-3 rounded-lg border p-3">
            <div className="min-w-0 flex-1">
              <p className="truncate text-sm font-medium">
                {m.mergedNames.join(', ')} → {m.primaryName}
              </p>
              <p className="text-xs text-muted-foreground">{dayjs(m.createdAt).format('DD/MM/YYYY HH:mm')}</p>
            </div>
            {m.undoneAt ? (
              <Badge variant="outline">{t('duplicates.history.undone')}</Badge>
            ) : (
              <Button variant="outline" size="sm" onClick={() => onUndo(m)} disabled={undo.isPending}>
                <Undo2 className="h-4 w-4" /> {t('duplicates.history.undo')}
              </Button>
            )}
          </div>
        ))}
      </CardContent>
    </Card>
  )
}

function DuplicateGroupCard({ group }: { group: DuplicateGroup }) {
  const { t } = useTranslation('custList')
  const merge = useMergeDuplicateGroup()
  const dismiss = useDismissDuplicateGroup()
  const [primaryId, setPrimaryId] = useState(group.contacts[0]?.id ?? '')

  const onMerge = () => {
    if (!primaryId) return
    if (!confirm(t('duplicates.group.confirmMerge'))) return
    merge.mutate(
      { groupId: group.id, primaryContactId: primaryId },
      {
        onSuccess: (r: { mergedCount?: number }) =>
          toast.success(t('duplicates.group.merged', { count: r.mergedCount ?? 0 })),
        onError: (e) => toast.error(apiError(e)),
      },
    )
  }

  const onDismiss = () =>
    dismiss.mutate(group.id, {
      onSuccess: () => toast.success(t('duplicates.group.dismissed')),
      onError: (e) => toast.error(apiError(e)),
    })

  return (
    <Card>
      <CardHeader className="flex-row items-center justify-between space-y-0">
        <CardTitle className="flex items-center gap-2 text-base">
          <Badge variant="warning">{t(`duplicates.match.${group.matchType}`, { defaultValue: group.matchType })}</Badge>
          <span className="text-sm font-normal text-muted-foreground">
            {t('duplicates.group.summary', { count: group.contacts.length, confidence: Math.round(group.confidence * 100) })}
          </span>
        </CardTitle>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={onDismiss} disabled={dismiss.isPending}>
            <X className="h-4 w-4" /> {t('common:actions.skip')}
          </Button>
          <Button size="sm" onClick={onMerge} disabled={merge.isPending || !primaryId}>
            <GitMerge className="h-4 w-4" /> {t('duplicates.group.mergeIntoPrimary')}
          </Button>
        </div>
      </CardHeader>
      <CardContent className="space-y-2">
        <p className="text-xs text-muted-foreground">{t('duplicates.group.pickPrimary')}</p>
        {group.contacts.map((c) => (
          <DuplicateRow
            key={c.id}
            contact={c}
            selected={primaryId === c.id}
            onSelect={() => setPrimaryId(c.id)}
          />
        ))}
      </CardContent>
    </Card>
  )
}

function DuplicateRow({
  contact,
  selected,
  onSelect,
}: {
  contact: DuplicateContact
  selected: boolean
  onSelect: () => void
}) {
  const { t } = useTranslation('custList')
  return (
    <label
      className={`flex cursor-pointer items-center gap-3 rounded-lg border p-3 transition-colors ${
        selected ? 'border-primary bg-primary/5' : 'hover:bg-muted/40'
      }`}
    >
      <input
        type="radio"
        checked={selected}
        onChange={onSelect}
        className="h-4 w-4 accent-primary"
      />
      <Avatar className="h-9 w-9">
        {contact.avatarUrl && <AvatarImage src={contact.avatarUrl} alt={contact.fullName ?? ''} />}
        <AvatarFallback>{initials(contact.fullName)}</AvatarFallback>
      </Avatar>
      <div className="min-w-0 flex-1">
        <p className="truncate font-medium">{contact.fullName || t('duplicates.row.noName')}</p>
        <p className="truncate text-xs text-muted-foreground">
          {[contact.phone, contact.email].filter(Boolean).join(' · ') || t('duplicates.row.noContact')}
        </p>
      </div>
      <div className="hidden shrink-0 items-center gap-2 sm:flex">
        <StageBadge stage={contact.lifecycleStage} />
        <span className="text-xs text-muted-foreground">
          {t('duplicates.row.conversations', { count: contact._count?.conversations ?? 0 })}
        </span>
        <span className="whitespace-nowrap text-xs text-muted-foreground">
          {dayjs(contact.createdAt).format('DD/MM/YYYY')}
        </span>
      </div>
    </label>
  )
}
